import React, { useState, useEffect, useRef } from 'react';
import { postAPI, getURL } from '../../../app/api';
import { Navigation, Autoplay } from 'swiper/modules';
import Swiper from 'swiper';
import 'swiper/css';
import 'swiper/css/navigation';


const AvatarIcon = ({ name, color }) => {
    return (
        <svg
            width="100%"
            height="100%"
            viewBox="0 0 200 200"
            style={{ display: 'block', backgroundColor: color }}
        >
            <circle cx="100" cy="100" r="100" fill={color} />
            <circle cx="100" cy="70" r="32" fill="#ffffff" />
            <ellipse cx="100" cy="145" rx="48" ry="32" fill="#ffffff" />
        </svg>
    );
};


const getAvatarColor = (name) => {
    const colors = ['#f09e06', '#3b82f6', '#8b5cf6', '#10b981', '#ef4444', '#6366f1', '#ec4899'];
    const index = name ? name.charCodeAt(0) % colors.length : 0;
    return colors[index];
};

export default function ExecutiveCommittee() {
    const swiperRef = useRef(null);
    const [currentSlide, setCurrentSlide] = useState(1);
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [totalSlides, setTotalSlides] = useState(0);

    // Fetch executive committee members from API
    useEffect(() => {
        const fetchMembers = async () => {
            try {
                setLoading(true);
                const response = await postAPI('executive-committee/listPublished', {});
                if (response?.status === 1 && response?.data?.length > 0) {
                    setMembers(response.data);
                    setTotalSlides(response.data.length);
                } else {
                    setMembers([]);
                    setTotalSlides(0);
                }
            } catch (error) {
                console.error('Error fetching executive committee:', error);
                setMembers([]);
                setTotalSlides(0);
            } finally {
                setLoading(false);
            }
        };

        fetchMembers();
    }, []);


    useEffect(() => {
        if (!loading && members.length > 0 && swiperRef.current) {
            const swiper = new Swiper('.executive-member-active', {
                modules: [Navigation],
                navigation: {
                    nextEl: '.exec-swiper-button-next',
                    prevEl: '.exec-swiper-button-prev'
                },
                loop: members.length > 3,
                slidesPerView: 3,
                spaceBetween: 30,
                speed: 800,
                watchOverflow: false,

                breakpoints: {
                    1400: { slidesPerView: 4 },
                    1200: { slidesPerView: 4 },
                    992: { slidesPerView: 3 },
                    768: { slidesPerView: 2 },
                    576: { slidesPerView: 2 },
                    0: { slidesPerView: 1 }
                }
            });

            swiper.on('slideChange', () => {
                setCurrentSlide(swiper.realIndex + 1);
            });

            return () => {
                if (swiper) swiper.destroy();
            };
        }
    }, [loading, members]);


    const getImageUrl = (imagePath) => {
        if (!imagePath) return null;
        if (imagePath.startsWith('http://') || imagePath.startsWith('https://') || imagePath.startsWith('data:')) {
            return imagePath;
        }
        const apiUrl = getURL()?.API;
        return `${apiUrl}${imagePath.startsWith('/') ? '' : '/'}${imagePath}`;
    };


    if (!loading && members.length === 0) {
        return null;
    }

    return (
        <>
            <section className="new-member-slider py-5">
                <div className="container">
                    <div className="row justify-content-center">
                        <div className="col-lg-9 heading text-center">
                            <h2>Executive Committee</h2>
                            <p>The Office Bearers of the B E College Ex-Students' Club are entrusted with the responsibility of managing the club's activities, upholding its values, and guiding its growth. Through their dedicated efforts, the club continues to strengthen its alumni network, organize cultural and social programs, and preserve the spirit of unity that defines the B E College family.</p>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-lg-12">
                            {loading ? (
                                <div className="text-center py-5">
                                    <div className="spinner-border text-primary" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    <div className="swiper executive-member-active" ref={swiperRef}>
                                        <div className="swiper-wrapper">
                                            {members.map((member, index) => {
                                                const memberName = member.name || 'Unknown Member';
                                                const avatarColor = getAvatarColor(memberName);
                                                const imageUrl = getImageUrl(member.image);

                                                return (
                                                    <div className="swiper-slide" key={index} style={{ height: 'auto' }}>
                                                        <div className="item" style={{
                                                            background: 'linear-gradient(180deg, #fff 0%, #f0f7ff 100%)',
                                                            borderRadius: '16px',
                                                            padding: '30px 20px',
                                                            textAlign: 'center',
                                                            boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                                                            height: '100%',
                                                            minHeight: '380px',
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            alignItems: 'center'
                                                        }}>

                                                            <div style={{
                                                                width: '200px',
                                                                height: '200px',
                                                                borderRadius: '50%',
                                                                overflow: 'hidden',
                                                                marginBottom: '20px',
                                                                border: '5px solid #fff',
                                                                boxShadow: '0 6px 20px rgba(0,0,0,0.15)',
                                                                flexShrink: 0,
                                                                position: 'relative',
                                                                backgroundColor: avatarColor
                                                            }}>

                                                                <div style={{
                                                                    position: 'absolute',
                                                                    top: 0,
                                                                    left: 0,
                                                                    width: '100%',
                                                                    height: '100%'
                                                                }}>
                                                                    <AvatarIcon name={memberName} color={avatarColor} />
                                                                </div>

                                                                {imageUrl && (
                                                                    <img
                                                                        src={imageUrl}
                                                                        alt={memberName}
                                                                        style={{
                                                                            width: '100%',
                                                                            height: '100%',
                                                                            objectFit: 'cover',
                                                                            objectPosition: 'center top',
                                                                            position: 'relative',
                                                                            zIndex: 1
                                                                        }}
                                                                        onError={(e) => {
                                                                            e.target.style.display = 'none';
                                                                        }}
                                                                    />
                                                                )}
                                                            </div>


                                                            <span style={{
                                                                display: 'inline-block',
                                                                backgroundColor: '#f09e06',
                                                                color: '#fff',
                                                                padding: '5px 15px',
                                                                borderRadius: '20px',
                                                                fontSize: '12px',
                                                                fontWeight: '600',
                                                                marginBottom: '12px'
                                                            }}>{member?.batch || 'Executive'}</span>


                                                            <h3 style={{
                                                                fontSize: '18px',
                                                                fontWeight: '700',
                                                                color: '#1a1a2e',
                                                                marginBottom: '15px',
                                                                lineHeight: '1.3'
                                                            }}>{memberName}</h3>


                                                            <p style={{
                                                                fontSize: '14px',
                                                                color: '#555',
                                                                margin: 0
                                                            }}><strong>Designation:</strong> {member?.designation || 'N/A'}</p>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                    <div className="swiper-navigation">
                                        <div className="exec-swiper-button-prev custom-prev"></div>
                                        <div className="member-counter">
                                            <span id="current-slide">{currentSlide}</span> / <span id="total-slide" style={{ color: '#f09e06', fontSize: '20px' }}>{totalSlides}</span>
                                        </div>
                                        <div className="exec-swiper-button-next custom-next"></div>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </section>
        </>
    )
}
